import { PAYMENT_NETWORK_WIRE, type BankPaymentNetwork } from '@klear/bank-account/constants'

export const STATEMENT_DESCRIPTOR_MAX_LENGTH_ACH = 10
export const STATEMENT_DESCRIPTOR_MAX_LENGTH_WIRE = 140

export const STATEMENT_DESCRIPTOR_PATTERN = /^[-#.$&* A-Za-z0-9]+$/

const PLATFORM_DEFAULT_STATEMENT_DESCRIPTORS = new Set([
  'klear',
  'klearpay',
  'transfer',
  'payment',
  'transfer from klear',
  'transfer to klear'
])

export function statementDescriptorMaxLength(network: BankPaymentNetwork): number {
  return network === PAYMENT_NETWORK_WIRE ? STATEMENT_DESCRIPTOR_MAX_LENGTH_WIRE : STATEMENT_DESCRIPTOR_MAX_LENGTH_ACH
}

export function isPlatformDefaultStatementDescriptor(value: string): boolean {
  return PLATFORM_DEFAULT_STATEMENT_DESCRIPTORS.has(value.trim().toLowerCase())
}

export type StatementDescriptorValidationResult = { ok: true } | { ok: false; message: string }

export function validateStatementDescriptor(
  value: string,
  network: BankPaymentNetwork
): StatementDescriptorValidationResult {
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return { ok: false, message: 'Payment reference cannot be empty' }
  }
  const maxLength = statementDescriptorMaxLength(network)
  if (trimmed.length > maxLength) {
    const networkLabel = network === PAYMENT_NETWORK_WIRE ? 'wire' : 'ACH'
    return {
      ok: false,
      message: `Payment reference must be ${maxLength} characters or fewer for ${networkLabel} payments`
    }
  }
  if (!STATEMENT_DESCRIPTOR_PATTERN.test(trimmed)) {
    return {
      ok: false,
      message: 'Payment reference can only include letters, numbers, spaces, and -#.$&*'
    }
  }
  return { ok: true }
}

export function resolveStatementDescriptor(
  userValue: string | undefined,
  fallback: string,
  network: BankPaymentNetwork
): string {
  const trimmed = userValue?.trim()
  if (trimmed != null && trimmed.length > 0) {
    const result = validateStatementDescriptor(trimmed, network)
    if (!result.ok) {
      throw new Error(result.message)
    }
    return trimmed
  }
  const fallbackResult = validateStatementDescriptor(fallback, network)
  if (!fallbackResult.ok) {
    throw new Error(fallbackResult.message)
  }
  return fallback.trim()
}

export function networkLabelForStatementDescriptor(network: BankPaymentNetwork): string {
  return network === PAYMENT_NETWORK_WIRE ? 'Wire' : 'ACH'
}
